import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/ui/PageLayout";
import { useApi } from "../apiHelpers";
import { buttonClass } from "../components/ui/Button";
import { SegTrack } from "../components/ui/SegTrack";
import { StatTile } from "../components/ui/Panel";
import EmptyState from "../components/ui/EmptyState";
import FadeIn from "../components/ui/FadeIn";
import { ListStack, ListRow } from "../components/ui/ListRow";

// Medal/rank display for top 3
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="w-8 h-8 rounded-full bg-yellow-500/20 border-2 border-yellow-500 flex items-center justify-center shadow-[0_0_12px_rgba(234,179,8,0.4)]">
        <span className="text-yellow-400 font-bold">1</span>
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-8 h-8 rounded-full bg-[rgb(187_187_187/0.2)] border-2 border-on-surface-variant flex items-center justify-center">
        <span className="text-on-surface font-bold">2</span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-8 h-8 rounded-full bg-orange-600/20 border-2 border-orange-600 flex items-center justify-center">
        <span className="text-orange-400 font-bold">3</span>
      </div>
    );
  }
  return <span className="text-on-surface-variant font-mono w-8 text-center">{rank}</span>;
};

// Star icon
const StarIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className={className}>
    <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
  </svg>
);

const Leaderboard = () => {
  const [allTimeLeaders, setAllTimeLeaders] = useState([]);
  const [thisYearLeaders, setThisYearLeaders] = useState([]);
  const [totalStars, setTotalStars] = useState(0);
  const [activeTab, setActiveTab] = useState("thisYear");

  const api = useApi();

  useEffect(() => {
    api.get(`/goldstars`)
      .then((data) => {
        const allTimeLeaderboard = processData(data);
        const thisYearLeaderboard = processData(filterThisYear(data));
        setAllTimeLeaders(allTimeLeaderboard);
        setThisYearLeaders(thisYearLeaderboard);

        const totalStarQty = data.reduce((acc, item) => acc + item.starqty, 0);
        setTotalStars(totalStarQty);
      })
      .catch((error) => console.log("Error fetching leaderboard:", error));
  }, []);

  const processData = (data) => {
    const winnerMap = new Map();
    data.forEach((item) => {
      // Use new winners array if available, fallback to legacy string split
      const winners = item.winners && item.winners.length > 0
        ? item.winners
        : (item.winner || '').split(" - ")
            .filter(name => name.trim())
            .map(name => ({ firstname: name.trim() }));

      winners.forEach((winner) => {
        if (!winner || !winner.firstname) return;
        const displayName = winner.lastname
          ? `${winner.firstname} ${winner.lastname}`
          : winner.firstname;
        winnerMap.set(
          displayName,
          (winnerMap.get(displayName) || 0) + item.starqty
        );
      });
    });
    return Array.from(winnerMap, ([winner, starQty]) => ({ winner, starQty }))
      .sort((a, b) => b.starQty - a.starQty);
  };

  const filterThisYear = (data) => {
    const currentYear = new Date().getFullYear();
    return data.filter(
      (item) => new Date(item.dateissued).getFullYear() === currentYear
    );
  };

  const leaders = activeTab === "thisYear" ? thisYearLeaders : allTimeLeaders;

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-2 md:pt-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <StarIcon className="w-8 h-8 text-primary drop-shadow-[0_0_8px_rgba(255,204,51,0.6)]" />
          <h1 className="text-2xl font-bold text-white">Leaderboard</h1>
        </div>
        <p className="text-on-surface-variant text-sm">Gold star rankings</p>
      </div>

      {/* Stats Card */}
      <StatTile
        label="Total Stars Awarded"
        value={totalStars}
        tone="accent"
        icon={<StarIcon className="w-8 h-8" />}
        className="mb-6"
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <SegTrack
          label="Leaderboard period"
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { key: "thisYear", label: "This Year", count: thisYearLeaders.length },
            { key: "allTime", label: "All Time", count: allTimeLeaders.length },
          ]}
        />
        <div className="flex-1" />
        <Link
          to="/timeline"
          className={buttonClass("tonal")}
        >
          View Timeline →
        </Link>
      </div>

      {/* Leaderboard Table */}
      <FadeIn key={activeTab}>
      {leaders.length === 0 ? (
        <EmptyState variant="panel">No stars awarded yet</EmptyState>
      ) : (
        <ListStack
          label="Leaderboard"
          items={leaders}
          getKey={(leader) => leader.winner}
          renderRow={(leader, index) => {
            const rank = index + 1;
            const isTop3 = rank <= 3;
            return (
              <ListRow
                tone={rank === 1 ? "accent" : undefined}
                leading={<RankBadge rank={rank} />}
                title={
                  <span className={`font-medium ${isTop3 ? "text-white" : "text-on-surface"}`}>
                    {leader.winner}
                  </span>
                }
                trailing={
                  <span className="flex items-center gap-2">
                    <span className={`font-bold ${rank === 1 ? "text-primary text-xl" : "text-yellow-400"}`}>
                      {leader.starQty}
                    </span>
                    <StarIcon className={`${rank === 1 ? "text-primary w-5 h-5" : "text-yellow-500/70 w-4 h-4"}`} />
                  </span>
                }
              />
            );
          }}
        />
      )}
      </FadeIn>
    </PageLayout>
  );
};

export default Leaderboard;
